import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import { Loader2 } from "lucide-react";

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function InviteUserDialog({ open, onOpenChange, onSuccess }: InviteUserDialogProps) {
  const { orgId, isMFO, isSFO, families } = useTenant();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [familyId, setFamilyId] = useState<string>("");
  const [familyRole, setFamilyRole] = useState<string>("FamilyViewer");
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Get current user ID for created_by field
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id || null);
    });
  }, []);

  // For SFO, auto-select the single family
  useEffect(() => {
    if (isSFO && families.length > 0 && !familyId) {
      setFamilyId(families[0].id);
    }
  }, [isSFO, families, familyId]);

  const handleInvite = async () => {
    if (!email.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide an email address",
        variant: "destructive",
      });
      return;
    }
    if (!orgId) {
      toast({
        title: "Error",
        description: "Organization not loaded. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (!userId) {
      toast({
        title: "Error",
        description: "Unable to verify your session. Please refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }

    // For invitations, we need a family selected
    const targetFamilyId = isSFO ? families[0]?.id : familyId;
    if (!targetFamilyId) {
      toast({
        title: "Select a family",
        description: "Please select which family to invite this user to",
        variant: "destructive",
      });
      return;
    }

    // Determine the family role (default to FamilyViewer if not specified)
    const targetFamilyRole = familyRole || "FamilyViewer";

    setIsLoading(true);
    try {
      // 1. Create invitation record in database
      const { data: invitation, error: insertError } = await supabase
        .from("family_invitations")
        .insert({
          org_id: orgId,
          family_id: targetFamilyId,
          email: email.toLowerCase().trim(),
          role: targetFamilyRole,
          created_by: userId,
        })
        .select()
        .single();

      if (insertError) {
        // Check for duplicate invitation
        if (insertError.code === "23505") {
          toast({
            title: "Already invited",
            description: "An invitation has already been sent to this email address",
            variant: "destructive",
          });
          return;
        }
        throw insertError;
      }

      // 2. Call edge function to send the email
      const { error: emailError } = await supabase.functions.invoke(
        "send-family-invitation",
        {
          body: { invitation_id: invitation.id },
        }
      );

      if (emailError) {
        console.error("Error sending invitation email:", emailError);
        // The invitation was created, but email failed
        toast({
          title: "Invitation created",
          description: "The invitation was saved, but we couldn't send the email. They can still use the invitation link.",
          variant: "default",
        });
      } else {
        toast({
          title: "Invitation sent",
          description: `Invitation email sent to ${email}`,
        });
      }

      // Reset form
      setEmail("");
      if (isMFO) setFamilyId("");
      setFamilyRole("FamilyViewer");
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error inviting user:", error);
      const message = error?.message || (typeof error === 'string' ? error : "Failed to send invitation");
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new user to your organization
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {isMFO && (
            <div className="space-y-2">
              <Label htmlFor="family">Assign to Family</Label>
              <Select value={familyId} onValueChange={setFamilyId}>
                <SelectTrigger id="family">
                  <SelectValue placeholder="Select family" />
                </SelectTrigger>
                <SelectContent>
                  {families.map((family) => (
                    <SelectItem key={family.id} value={family.id}>
                      {family.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select which family to invite this user to
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="family-role">Access Level</Label>
            <Select value={familyRole} onValueChange={setFamilyRole}>
              <SelectTrigger id="family-role">
                <SelectValue placeholder="Select access level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FamilyAdmin">Admin - Full access to family data</SelectItem>
                <SelectItem value="FamilyManager">Manager - Manage family data</SelectItem>
                <SelectItem value="FamilyViewer">Viewer - Read-only access</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              What level of access should this user have?
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={isLoading || !email}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Invitation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
